import { CONTACT_EMAIL } from "@/components/v2/links";
import type { DocMeta } from "./types";

// =============================================================================
// STATEMENT WORDING: first-publish version.
// The text is copied verbatim from "Task 5: Statement page wording,
// first-publish version (X4)" in
// ~/lg/docs/exec-plans/2026-10-02-accessibility-5-documents-testing-answers.md.
// Task 14 of that plan replaces sections 3 and 4 and `lastUpdated` after the
// manual test pass. It finds this file by the id "accessibility-our-commitment",
// so keep the five section ids exactly as they are.
// Rules: no customer or partner names, no long dashes, nothing that has not
// actually been done.
// =============================================================================

export const ACCESSIBILITY_STATEMENT_META: DocMeta = {
  slug: "accessibility",
  title: "Accessibility Statement",
  subtitle:
    "How Logical Health works to make its website and services usable for everyone",
  description:
    "Our accessibility target, how we test, known limitations, and how to report a problem.",
  lastUpdated: "October 2, 2026",
  sections: [
    { id: "accessibility-our-commitment", title: "Our commitment" },
    {
      id: "accessibility-the-standard-we-follow",
      title: "The standard we follow",
    },
    { id: "accessibility-how-we-test", title: "How we test" },
    { id: "accessibility-known-limitations", title: "Known limitations" },
    {
      id: "accessibility-tell-us-about-a-problem",
      title: "Tell us about a problem",
    },
  ],
};

export function AccessibilityStatement() {
  return (
    <>
      <section id="accessibility-our-commitment" className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          Our commitment
        </h2>
        <p>
          Logical Health wants everyone to be able to use our website, the
          pages we send to participants, our messages, and the dashboard our
          customers use. That includes people who use a screen reader, a
          keyboard instead of a mouse, larger text, or other assistive
          technology.
        </p>
      </section>

      <section id="accessibility-the-standard-we-follow" className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          The standard we follow
        </h2>
        <p>
          Our target is the Web Content Accessibility Guidelines (WCAG) 2.1 at
          Level A and Level AA. It applies to this website, to the pages
          participants open from a message, to the staff dashboard, and to the
          content of the messages we send.
        </p>
      </section>

      <section id="accessibility-how-we-test" className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">How we test</h2>
        <p>
          Automated accessibility checks run on every change to this
          website&apos;s code and must pass before the change is released.
          Automated scans of this website&apos;s pages run against the WCAG
          2.1 Level A and AA rules. Next, we will test our website and
          services by hand with a keyboard and with screen readers. An
          independent audit is planned before the end of 2026. We will update
          this page with the results.
        </p>
      </section>

      <section id="accessibility-known-limitations" className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          Known limitations
        </h2>
        <p>
          We have fixed the barriers we know of on this website. Our review of
          our other services is still in progress, and we will list any
          remaining limitations here when it is done.
        </p>
        <p>
          Our demo scheduling links open a booking page run by another company.
          We do not control how that page works.
        </p>
      </section>

      <section id="accessibility-tell-us-about-a-problem" className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          Tell us about a problem
        </h2>
        <p>
          If you have trouble using any part of our website or services, or
          you need information in a different format, email{" "}
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            {CONTACT_EMAIL}
          </a>
          . Please tell us the page or message, what you were trying to do,
          and the device and assistive technology you use, if any. Every
          report goes to the person responsible for accessibility at Logical
          Health.
        </p>
      </section>
    </>
  );
}
